import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Check, Trash2, Circle } from "lucide-react";
import EpqMilestones from "@/components/ascend/EpqMilestones";

export default function Tasks() {
  const [subjects, setSubjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newDue, setNewDue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subjects");
        const items = data.items || [];
        setSubjects(items);
        if (items[0]) setActiveId(items[0].id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/tasks?subject_id=${activeId}`);
        setTasks(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [activeId]);

  const activeSubject = useMemo(() => subjects.find((s) => s.id === activeId), [subjects, activeId]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !activeId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tasks", {
        subject_id: activeId,
        title: newTitle.trim(),
        due_date: newDue || null,
      });
      setTasks((arr) => [data, ...arr]);
      setNewTitle("");
      setNewDue("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (t) => {
    try {
      const { data } = await api.patch(`/tasks/${t.id}`, { is_done: !t.is_done });
      setTasks((arr) => arr.map((x) => (x.id === t.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeTask = async (t) => {
    try {
      await api.delete(`/tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <AppShell><div className="text-muted">Loading…</div></AppShell>;

  return (
    <AppShell>
      <Toaster richColors position="top-center" />

      <div className="pt-2 mb-6 fade-up">
        <span className="eyebrow">what's on your plate</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Tasks</h1>
      </div>

      {/* Subject tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="task-subject-tabs">
        {subjects.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveId(s.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
            style={{
              background: activeId === s.id ? `color-mix(in oklab, ${s.color} 22%, var(--surface))` : "var(--surface-2)",
              border: `1px solid ${activeId === s.id ? s.color : "var(--border)"}`,
              color: "var(--text)",
            }}
            data-testid={`task-tab-${s.name.toLowerCase()}`}
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
            {s.name}
          </button>
        ))}
      </div>

      {/* EPQ milestone tracker */}
      {activeSubject?.is_milestone_based && (
        <div className="mb-8 fade-up" data-testid="epq-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-2xl sm:text-3xl" style={{ color: "var(--text)" }}>EPQ milestones</h2>
            <span className="font-hand text-base" style={{ color: "var(--accent)" }}>tap the ring to update</span>
          </div>
          <EpqMilestones />
          <div className="divider-soft my-8" />
          <h3 className="font-display text-xl mb-3" style={{ color: "var(--text)" }}>Extra tasks</h3>
        </div>
      )}

      {/* Add form */}
      <form onSubmit={addTask} className="card-soft p-5 mb-6 flex flex-col sm:flex-row gap-3" data-testid="task-add-form">
        <input
          className="input-soft flex-1"
          placeholder={activeSubject ? `Add a task for ${activeSubject.name}…` : "Add a task…"}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          maxLength={200}
          data-testid="task-title-input"
        />
        <input
          type="date"
          className="input-soft sm:w-44"
          value={newDue}
          onChange={(e) => setNewDue(e.target.value)}
          data-testid="task-due-input"
        />
        <button type="submit" className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60" disabled={busy || !newTitle.trim()} data-testid="task-add-btn">
          <Plus size={14} strokeWidth={1.75} /> Add
        </button>
      </form>

      {/* List */}
      <div className="space-y-2" data-testid="task-list">
        {tasks.length === 0 && (
          <div className="text-center py-10 font-hand text-lg text-muted">
            nothing yet — a blank page is a good place.
          </div>
        )}
        {tasks.map((t) => (
          <div
            key={t.id}
            className="card-soft-2 p-4 flex items-center gap-3"
            style={{ opacity: t.is_done ? 0.6 : 1 }}
            data-testid={`task-item-${t.id}`}
          >
            <button
              onClick={() => toggleDone(t)}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
              style={{
                background: t.is_done ? "var(--primary)" : "var(--surface)",
                color: t.is_done ? "#fff" : "var(--text-muted)",
                border: "1px solid var(--border)",
              }}
              data-testid={`task-toggle-${t.id}`}
              aria-label={t.is_done ? "Mark as not done" : "Mark done"}
            >
              {t.is_done ? <Check size={14} strokeWidth={2.5} /> : <Circle size={14} strokeWidth={1.5} />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-sm sm:text-base ${t.is_done ? "line-through" : ""}`} style={{ color: "var(--text)" }}>
                {t.title}
              </div>
              {t.due_date && (
                <div className="text-xs text-muted mt-0.5">due {t.due_date}</div>
              )}
            </div>
            <button
              onClick={() => removeTask(t)}
              className="p-2 rounded-full hover:bg-[var(--surface)]"
              data-testid={`task-delete-${t.id}`}
              aria-label="Delete task"
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
